import { ApplicationElement, html } from '../application_element/element.js';
import { csrfFetch } from '../../lib/helpers.js';
import Snack from '@effectivastudio/snackbar';
import '../progress_bar/element.js';
import '../icon/element.js';

/**
 * Used for PollCard
 *
 * If you don't pass percent it will skip rendering progress bar
 *
 * @prop rid - Main resource id (eg. @poll_item.id from rails)
 * @prop type - Input type (options: radio, checkbox)
 */
export default class PollItem extends ApplicationElement {
  static properties = {
    rid: { type: Number },
    type: { type: String },
    cardId: { type: Number },
    label: { type: String },
    percent: { type: Number },
    checked: { type: Boolean },
    disabled: { type: Boolean },
  };

  constructor() {
    super();
    this.type = 'radio';
    this.checked = false;
    this.disabled = false;
  }

  createRenderRoot() {
    return this;
  }

  /** @internal */
  get domId() {
    return `o_${this.rid}`;
  }

  /** @internal */
  get href() {
    return `/cards/${this.cardId}/poll_options/${this.rid}/vote`;
  }

  /** @internal */
  get inputSelector() {
    return 'custom-input-t1';
  }

  /** @internal */
  get shouldRenderInput() {
    return !this.disabled;
  }

  /** @internal */
  get shouldRenderIcon() {
    return !this.shouldRenderInput && this.checked;
  }

  /** @internal */
  get localizedPercent() {
    return this.i18n.numberToPercentage(this.percent, {
      precision: 2,
      stripInsignificantZeros: true,
    });
  }

  onChange(e) {
    this.checked = e.target.checked;
    this.fetch();
  }

  fetch() {
    const event = new Event('ajax:beforeSend', {
      bubbles: true,
      composed: true,
      cancelable: true,
    });
    this.dispatchEvent(event);

    if (event.defaultPrevented) return;

    csrfFetch(this.href, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'post',
    })
      .then(res => this.onFetchSuccess(res))
      .catch(res => this.onFetchError(res));
  }

  onFetchError() {
    this.dispatchEvent(
      new Event('ajax:error', { bubbles: true, composed: true })
    );
    this.dispatchEvent(
      new Event('ajax:complete', { bubbles: true, composed: true })
    );
    this.displaySnackBarError();
    this.updateActiveState();
  }

  onFetchSuccess() {
    this.dispatchEvent(
      new Event('ajax:success', { bubbles: true, composed: true })
    );
    this.dispatchEvent(
      new Event('ajax:complete', { bubbles: true, composed: true })
    );

    Snack({
      text: this.t('snack.success_msg'),
    });
  }

  displaySnackBarError() {
    Snack({
      text: this.t('snack.generic_error_msg'),
      customClass: 'snackbar-danger',
    });
  }

  renderProgressBar() {
    return html`
      <span class="percent">${this.localizedPercent}</span>
      <ef-progress-bar percent="${this.percent}"></ef-progress-bar>
    `;
  }

  renderIcon() {
    return html`<ef-icon name="check"></ef-icon> `;
  }

  renderInput() {
    return html`
      <input
        type=${this.type}
        id=${this.domId}
        name="${this.cardId}"
        value=${this.rid}
        class=${this.inputSelector}
        ?checked=${this.checked}
        autocomplete="off"
        @change=${this.onChange} />
    `;
  }

  render() {
    return html`
      ${this.shouldRenderIcon ? this.renderIcon() : null}
      ${this.shouldRenderInput ? this.renderInput() : null}
      <label for=${this.domId}>${this.label}</label>
      ${this.percent >= 0 ? this.renderProgressBar() : null}
    `;
  }
}

PollItem.registerTagName('ef-poll-item');

customElements.get('ef-poll-item') ||
  customElements.define('ef-poll-item', PollItem);
